import { Box, Card, List, ListItem, Typography } from '@veraclins-dev/ui';

import type { CaseStudy as CaseStudyData } from '../../content/case-studies';
import { AppLink } from '../../link';

const chipClassName =
  'rounded-lg border border-primary/14 bg-[color-mix(in_oklch,var(--primary)_10%,var(--card))] px-[11px] py-[5px] text-xs/normal font-semibold text-foreground';

const brandButtonClassName =
  'inline-flex items-center justify-center rounded-lg border border-primary bg-primary px-5.5 py-3 text-base font-semibold text-primary-foreground transition hover:-translate-y-px';

const outlineButtonClassName =
  'inline-flex items-center justify-center rounded-lg border border-primary/30 bg-transparent px-5.5 py-3 text-base font-semibold text-foreground transition hover:-translate-y-px hover:border-primary/60';

const ctaRowClassName =
  'flex w-full gap-3.5 border-t border-primary/12 pt-8 mt-10';

export function CaseStudy({ caseStudy }: { caseStudy: CaseStudyData }) {
  return (
    <Box
      component="article"
      mx="auto"
      display="flex"
      flexDirection="column"
      items="start"
      pt={12}
      pb={10}
      // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 880px article width has no maxW scale-token/prop equivalent
      className="max-w-220"
    >
      <AppLink
        to="/work"
        tone="brand"
        mb={7}
        className="text-sm font-semibold text-primary"
      >
        ← All work
      </AppLink>

      <Typography
        component="p"
        mb={3}
        className="text-xs font-bold uppercase tracking-widest text-primary"
      >
        {caseStudy.kind}
      </Typography>
      <Typography
        variant="h1"
        mb={3}
        className="font-sans text-5xl font-extrabold leading-tight tracking-tight"
      >
        {caseStudy.title}
      </Typography>
      <Typography
        component="p"
        mb={4}
        // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 60ch max-width has no maxW scale-token/prop equivalent
        className="max-w-[60ch] text-xl leading-snug text-foreground/75"
      >
        {caseStudy.tagline}
      </Typography>
      <Typography component="p" mb={8} className="text-sm text-foreground/60">
        {caseStudy.meta}
      </Typography>

      <Box display="grid" gridCols={3} w="full" gap={4} mb={9}>
        {caseStudy.metrics.map((metric) => (
          <Card
            key={metric.label}
            p={5}
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 16px radius and primary-tinted border have no scale-token/prop equivalent
            className="rounded-2xl border-primary/12 text-center"
          >
            <Typography
              component="span"
              display="block"
              className="text-2xl font-extrabold tracking-tight text-primary"
            >
              {metric.value}
            </Typography>
            <Typography
              component="span"
              display="block"
              mt={1}
              className="text-xs/normal text-foreground/60"
            >
              {metric.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {caseStudy.blocks.map((block) => (
        <Box key={block.heading} mb={8}>
          <Typography
            variant="h2"
            mb={2}
            className="text-xl/normal font-extrabold tracking-normal"
          >
            {block.heading}
          </Typography>
          <Typography
            component="p"
            className="text-base leading-relaxed text-foreground/80"
          >
            {block.body}
          </Typography>
          {block.bullets ? (
            <List variant="unordered-disc" mt={3} pl={5} className="space-y-2">
              {block.bullets.map((bullet) => (
                <ListItem
                  key={bullet}
                  className="text-base leading-relaxed text-foreground/80"
                >
                  {bullet}
                </ListItem>
              ))}
            </List>
          ) : null}
        </Box>
      ))}

      <Box mb={10}>
        <Typography
          component="p"
          mb={3}
          className="text-xs font-bold uppercase tracking-widest text-foreground/55"
        >
          Stack
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {caseStudy.stack.map((tech) => (
            <Typography
              key={tech}
              component="span"
              variant="inherit"
              className={chipClassName}
            >
              {tech}
            </Typography>
          ))}
        </Box>
      </Box>

      <Box className={ctaRowClassName}>
        <AppLink to="/work" tone="neutral" className={brandButtonClassName}>
          See more work
        </AppLink>
        <AppLink
          to="/contact"
          tone="neutral"
          className={outlineButtonClassName}
        >
          Get in touch
        </AppLink>
      </Box>
    </Box>
  );
}
